import { useState } from 'react';
import {
  type MaintenanceStatus,
  type MaintenanceType,
  type MaintenanceModule,
  statusMeta,
  moduleMeta,
  allModules,
} from '@/mocks/saGlobalMaintenanceCenter';

interface MaintenanceFiltersProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  activeStatus: string;
  onStatusChange: (v: string) => void;
  activeType: string;
  onTypeChange: (v: string) => void;
  activeModule: string;
  onModuleChange: (v: string) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  statusCounts: Record<string, number>;
  typeCounts: Record<string, number>;
  moduleCounts: Record<string, number>;
}

export default function MaintenanceFilters({
  searchTerm, onSearchChange,
  activeStatus, onStatusChange,
  activeType, onTypeChange,
  activeModule, onModuleChange,
  dateFrom, onDateFromChange,
  dateTo, onDateToChange,
  statusCounts, typeCounts, moduleCounts,
}: MaintenanceFiltersProps) {
  const hasFilters = activeStatus !== 'all' || activeType !== 'all' || activeModule !== 'all' || !!dateFrom || !!dateTo || !!searchTerm;

  return (
    <div className="space-y-3">
      {/* Search + clear */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por nombre, ID, descripción, creador..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-800 text-xs placeholder:text-foreground-400 outline-none focus:border-background-400 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-circle-line text-sm"></i>
            </button>
          )}
        </div>
        {hasFilters && (
          <button
            onClick={() => {
              onStatusChange('all');
              onTypeChange('all');
              onModuleChange('all');
              onDateFromChange('');
              onDateToChange('');
              onSearchChange('');
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-500 text-xs font-semibold hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-off-line text-sm"></i>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-foreground-400 text-[10px] font-medium mr-1">Estado:</span>
        {(['all', 'active', 'scheduled', 'completed', 'cancelled'] as const).map((st) => {
          const count = st === 'all' ? statusCounts.all : (statusCounts[st] || 0);
          if (count === 0 && st !== 'all') return null;
          const isActive = activeStatus === st;
          const meta = st === 'all' ? { label: 'Todos', icon: '' } : statusMeta[st as MaintenanceStatus];
          return (
            <button
              key={st}
              onClick={() => onStatusChange(st)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-background-300'
              }`}
            >
              {st !== 'all' && <i className={`${meta.icon} text-[10px]`}></i>}
              {meta.label || st}
              <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Type filter */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-foreground-400 text-[10px] font-medium mr-1">Tipo:</span>
        {(['all', 'global', 'per_module'] as const).map((tp) => {
          const count = tp === 'all' ? typeCounts.all : (typeCounts[tp] || 0);
          if (count === 0 && tp !== 'all') return null;
          const isActive = activeType === tp;
          const label = tp === 'all' ? 'Todos' : tp === 'global' ? 'Global' : 'Por Módulo';
          const icon = tp === 'global' ? 'ri-global-line' : tp === 'per_module' ? 'ri-stack-line' : '';
          return (
            <button
              key={tp}
              onClick={() => onTypeChange(tp)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-background-300'
              }`}
            >
              {tp !== 'all' && <i className={`${icon} text-[10px]`}></i>}
              {label}
              <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Module filter */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-foreground-400 text-[10px] font-medium mr-1">Módulo:</span>
        {(['all', ...allModules] as const).map((md) => {
          const count = md === 'all' ? moduleCounts.all : (moduleCounts[md] || 0);
          if (count === 0 && md !== 'all') return null;
          const isActive = activeModule === md;
          const meta = md === 'all' ? { label: 'Todos', icon: 'ri-apps-line', color: '' } : moduleMeta[md as MaintenanceModule];
          return (
            <button
              key={md}
              onClick={() => onModuleChange(md)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-background-300'
              }`}
            >
              <i className={`${meta.icon} text-[10px]`}></i>
              {meta.label}
              <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Date range */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <label className="text-foreground-400 text-[10px] whitespace-nowrap">Desde:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="px-2.5 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-700 text-xs outline-none focus:border-background-400 cursor-pointer transition-colors"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-foreground-400 text-[10px] whitespace-nowrap">Hasta:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="px-2.5 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-700 text-xs outline-none focus:border-background-400 cursor-pointer transition-colors"
          />
        </div>
      </div>
    </div>
  );
}